import React, { Component, ReactNode } from 'react';
import { submitBugReport } from '@/services/bugReportService';
import { redactRoute, redactEmbedded } from '@/lib/redactRoute';
import {
  readLastReloadAt,
  recordReloadAttempt,
  shouldReloadOnPreloadError,
} from '@/lib/preloadRecovery';
import {
  AUTO_RELOAD_DELAY_MS,
  isChunkLoadError,
  prefersReducedMotion,
} from './chunkLoadError';
import {
  ERROR_VARIANTS,
  UPGRADE_VARIANTS,
  findError,
  findUpgrade,
  pickRandom,
  type ErrorVariant,
  type UpgradeVariant,
} from './fallbackVariants';
import {
  RealErrorFallbackScreen,
  UpgradeFallbackScreen,
} from './FallbackScreens';
import './lazy-load-fallback.css';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
  reported: boolean;
  upgradeVariant: UpgradeVariant;
  errorVariant: ErrorVariant;
}

const DEV_PREVIEW_ERROR = Object.assign(new Error("Cannot read properties of undefined (reading 'group_id')"), {
  stack: undefined,
});

function readDevPreview(): { kind: 'upgrade'; variant: UpgradeVariant } | { kind: 'error'; variant: ErrorVariant } | null {
  if (!import.meta.env.DEV || import.meta.env.MODE === 'test' || typeof window === 'undefined') {
    return null;
  }
  const params = new URLSearchParams(window.location.search);
  const kind = params.get('tk-fallback');
  const skin = params.get('tk-skin');
  if (kind === 'upgrade') return { kind, variant: findUpgrade(skin) };
  if (kind === 'error') return { kind, variant: findError(skin) };
  return null;
}

export class LazyLoadErrorBoundary extends Component<Props, State> {
  private reloadTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      isChunkError: false,
      reported: false,
      upgradeVariant: UPGRADE_VARIANTS[0]!,
      errorVariant: ERROR_VARIANTS[0]!,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      isChunkError: isChunkLoadError(error),
      upgradeVariant: pickRandom(UPGRADE_VARIANTS),
      errorVariant: pickRandom(ERROR_VARIANTS),
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Lazy load error:', error, errorInfo);

    if (this.state.isChunkError) {
      const shouldReload = shouldReloadOnPreloadError({
        online: navigator.onLine,
        lastReloadAt: readLastReloadAt(),
        now: Date.now(),
      });
      if (shouldReload && !prefersReducedMotion()) {
        this.reloadTimer = setTimeout(() => {
          recordReloadAttempt(Date.now());
          window.location.reload();
        }, AUTO_RELOAD_DELAY_MS);
      }
      return;
    }

    this.reportBug(error);
  }

  componentWillUnmount() {
    if (this.reloadTimer) clearTimeout(this.reloadTimer);
  }

  reportBug = (error: Error) => {
    const route = redactRoute(window.location.pathname);
    const message = redactEmbedded(error.message);
    const dedupeKey = `tk-bug-reported:${route}:${message}`;
    if (window.sessionStorage.getItem(dedupeKey) === '1') return;
    window.sessionStorage.setItem(dedupeKey, '1');

    submitBugReport({ note: `Auto-report: page crash on ${route} — ${message}` })
      .then(() => this.setState({ reported: true }))
      .catch(console.error);
  };

  handleReload = () => {
    if (this.reloadTimer) {
      clearTimeout(this.reloadTimer);
      this.reloadTimer = null;
    }
    if (this.state.isChunkError) {
      recordReloadAttempt(Date.now());
    }
    window.location.reload();
  };

  handleGoBack = () => {
    window.history.back();
  };

  render() {
    const preview = readDevPreview();
    if (preview?.kind === 'upgrade') {
      return (
        <UpgradeFallbackScreen
          variant={preview.variant}
          onReload={this.handleReload}
          onBack={this.handleGoBack}
        />
      );
    }
    if (preview?.kind === 'error') {
      return (
        <RealErrorFallbackScreen
          variant={preview.variant}
          error={this.state.error ?? DEV_PREVIEW_ERROR}
          reported={false}
          onReload={this.handleReload}
          onBack={this.handleGoBack}
        />
      );
    }

    if (this.state.hasError) {
      if (this.state.isChunkError) {
        return (
          <UpgradeFallbackScreen
            variant={this.state.upgradeVariant}
            onReload={this.handleReload}
            onBack={this.handleGoBack}
          />
        );
      }

      return (
        <RealErrorFallbackScreen
          variant={this.state.errorVariant}
          error={this.state.error}
          reported={this.state.reported}
          onReload={this.handleReload}
          onBack={this.handleGoBack}
        />
      );
    }

    return this.props.children;
  }
}
